// components/industry-templates/LiteratureEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Pen, Users, Award, Book } from 'lucide-react';

export const LiteratureEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Event Description */}
      <Card className="border-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-2xl">
            <BookOpen className="w-8 h-8" style={{ color: theme.primary }} />
            About This Literature Festival
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 text-lg leading-relaxed mb-6">{structure.description}</p>
          
          {/* Show author count if available */}
          {industryData?.authors && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 bg-white/10 rounded-xl text-center">
                <div className="text-4xl mb-2">✍️</div>
                <p className="text-4xl font-bold" style={{ color: theme.primary }}>{industryData.authors}+</p>
                <p className="text-white mt-2">Featured Authors</p>
              </div>
              {industryData?.books && (
                <div className="p-5 bg-white/10 rounded-xl text-center">
                  <div className="text-4xl mb-2">📚</div>
                  <p className="text-4xl font-bold" style={{ color: theme.primary }}>∞</p>
                  <p className="text-white mt-2">Books Available</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Topics Covered */}
      {structure.topics && structure.topics.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Book className="w-6 h-6" style={{ color: theme.primary }} />
              Topics & Focus Areas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {structure.topics.map((topic: string, idx: number) => (
                <Badge key={idx} style={{ backgroundColor: theme.primary }} className="text-lg py-3 px-6">
                  {topic}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Literature Festival Highlights */}
      <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Pen className="w-6 h-6" style={{ color: theme.primary }} />
            Festival Highlights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: '📖', title: 'Author Readings', desc: 'Live reading sessions by renowned authors' },
              { icon: '✍️', title: 'Book Signings', desc: 'Meet your favorite authors in person' },
              { icon: '🎤', title: 'Panel Discussions', desc: 'Engage with literary experts and critics' },
              { icon: '🏆', title: 'Literary Awards', desc: 'Celebration of outstanding literary works' },
              { icon: '📚', title: 'Book Fair', desc: 'Browse and purchase from wide collection' },
              { icon: '👥', title: 'Networking', desc: 'Connect with publishers, agents, and writers' }
            ].map((highlight, idx) => (
              <div key={idx} className="p-4 bg-white/10 rounded-lg border-l-4 hover:bg-white/15 transition-colors" style={{ borderColor: theme.primary }}>
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{highlight.icon}</div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{highlight.title}</h4>
                    <p className="text-sm text-slate-400">{highlight.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Writing Workshops */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-6 h-6" style={{ color: theme.primary }} />
            Writing Workshops & Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              'Creative Writing Masterclass',
              'Poetry Writing Workshop',
              'Novel Writing Techniques',
              'Publishing & Marketing'
            ].map((workshop, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg flex items-center gap-3 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primary}30` }}>
                  <span style={{ color: theme.primary }}>✓</span>
                </div>
                <p className="text-white font-medium">{workshop}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Event Tags */}
      {structure.tags && structure.tags.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Event Tags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {structure.tags.map((tag: string, idx: number) => (
                <Badge key={idx} variant="outline" className="border-white/20 text-white text-sm py-2 px-4">
                  #{tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizer Information */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white">Organized By</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl" style={{ backgroundColor: theme.primary }}>
              📚
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white text-xl mb-2">{structure.organizer_name}</h4>
              <div className="space-y-2 text-slate-300">
                {structure.organizer_email && (
                  <p className="flex items-center gap-2">
                    <span>📧</span> {structure.organizer_email}
                  </p>
                )}
                {structure.organizer_phone && (
                  <p className="flex items-center gap-2">
                    <span>📞</span> {structure.organizer_phone}
                  </p>
                )}
                {structure.organizer_website && (
                  <a href={structure.organizer_website} target="_blank" rel="noopener noreferrer" 
                     className="flex items-center gap-2 text-blue-400 hover:underline">
                    <span>🌐</span> {structure.organizer_website}
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
